import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Camera, Droplet, Flame, MapPin, Mail } from "lucide-react";
import NomCharacter from "@/components/NomCharacter";
import { COMPANY, scrollToId } from "@/lib/site";

const EASE = [0.16, 1, 0.3, 1];

function Ring({ label, value, pct, color }) {
  const r = 17;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg viewBox="0 0 44 44" className="h-11 w-11 -rotate-90">
        <circle cx="22" cy="22" r={r} fill="none" stroke="#F3E4DA" strokeWidth="6" />
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
        />
      </svg>
      <span className="text-[10px] font-extrabold leading-none text-nom-inkSoft">{label}</span>
      <span className="text-[10px] font-bold leading-none" style={{ color }}>{value}</span>
    </div>
  );
}

function PhoneMock() {
  return (
    <div className="relative mx-auto w-64 sm:w-72" data-testid="hero-phone-mock">
      <div className="rounded-[48px] border-[6px] border-nom-ink bg-nom-cream p-4 shadow-nomLg">
        <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-nom-ink/20" />
        <div className="flex items-center justify-between">
          <p className="font-display text-lg font-semibold text-nom-ink">Today</p>
          <span className="inline-flex items-center gap-1 rounded-full bg-nom-sunny px-2.5 py-1 text-[11px] font-extrabold text-nom-ink">
            <Flame className="h-3 w-3" aria-hidden="true" /> Day 1
          </span>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-1 rounded-3xl border border-nom-border bg-white p-3">
          <Ring label="Calories" value="75%" pct={0.75} color="#FF7369" />
          <Ring label="Protein" value="60%" pct={0.6} color="#7C5CFF" />
          <Ring label="Carbs" value="45%" pct={0.45} color="#F2A93B" />
          <Ring label="Fat" value="30%" pct={0.3} color="#2EC4B6" />
        </div>
        <div className="dot-grid mt-3 flex flex-col items-center rounded-3xl bg-nom-peach/70 px-4 pb-4 pt-3">
          <NomCharacter className="h-24 w-24" />
          <div className="-mt-1 rounded-2xl rounded-bl-md bg-white px-3 py-1.5 text-[11px] font-bold text-nom-ink shadow-nom">
            You're doing great!
          </div>
        </div>
        <div className="mt-3 flex items-center justify-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-nom-coral px-4 py-2 text-xs font-extrabold text-white">
            <Camera className="h-3.5 w-3.5" aria-hidden="true" /> Scan meal
          </span>
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-nom-water text-white">
            <Droplet className="h-4 w-4" aria-hidden="true" />
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  const lines = [
    { text: "Wellness that", coral: false },
    { text: "feels like play.", coral: true },
  ];

  return (
    <section ref={ref} className="relative overflow-hidden pb-20 pt-32 sm:pt-36 lg:pb-28" data-testid="hero-section">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -right-32 -top-24 h-96 w-96 rounded-full bg-nom-coral/15 blur-3xl" />
        <div className="absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-nom-sunny/25 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-nom-border bg-white px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-nom-inkSoft shadow-nom"
            data-testid="hero-status-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-nom-coral opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-nom-coral" />
            </span>
            NomNom is now live
          </motion.span>

          <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.06] tracking-tight text-nom-ink sm:text-5xl lg:text-6xl">
            {lines.map((line, i) => (
              <span key={line.text} className="reveal-line">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                  className={line.coral ? "text-nom-coral" : "text-nom-ink"}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-nom-inkSoft sm:text-lg"
            data-testid="hero-subline"
          >
            {COMPANY.legalName} is a Wisconsin software company. NomNom — our friendly nutrition and wellness
            companion — makes healthy habits feel like taking care of someone who matters. Available now on the App
            Store and Google Play.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="#nomnom"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("nomnom");
              }}
              data-testid="hero-nomnom-cta"
              className="inline-flex items-center gap-2 rounded-full bg-nom-coral px-7 py-3.5 font-display text-base font-semibold text-white shadow-coral transition-all duration-200 hover:-translate-y-0.5 hover:bg-nom-coralDark active:scale-[0.98]"
            >
              Meet NomNom
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="hero-contact-cta"
              className="inline-flex items-center gap-2 rounded-full border border-nom-borderStrong bg-white px-7 py-3.5 font-display text-base font-semibold text-nom-ink transition-all duration-200 hover:-translate-y-0.5 hover:border-nom-coral hover:text-nom-coral active:scale-[0.98]"
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              Get in touch
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-nom-muted"
            data-testid="hero-location-line"
          >
            <MapPin className="h-4 w-4 text-nom-coral" aria-hidden="true" />
            From Wisconsin
          </motion.p>
        </div>

        <motion.div style={{ y: phoneY }} className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 5 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ type: "spring", stiffness: 60, damping: 14, delay: 0.45 }}
          >
            <PhoneMock />
            <p className="mt-4 text-center text-xs font-bold text-nom-muted" data-testid="hero-app-caption">
              NomNom: AI Calorie Tracker — app preview
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
